import React from "react";
import TopToolbar from "../../components/TopToolbar";
import {
  Alert,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { alpha } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ColorsEnum } from "../../common/theme/colors";
import { AlertRow, Patient } from "./type";
import { ROUTES } from "../../common/constant";
import { getUserPatients } from "./requests";
import { getSharedWithMe } from "../../components/ShareConversation/requests";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";

const formatDate = (value: string, locale: string = 'en-US') => {
  const localeMap: Record<string, string> = { en: 'en-US', fr: 'fr-FR', no: 'nb-NO' };
  return new Intl.DateTimeFormat(localeMap[locale] || 'en-US', {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [data, setData] = React.useState<Patient[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [errorSnackbar, setErrorSnackbar] = React.useState<string | null>(null);
  const [sharedWithMe, setSharedWithMe] = React.useState<
    { conversation_id: string; title: string; updated_at: string; patient_id: string; short_code: string }[]
  >([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const [menuPatientId, setMenuPatientId] = React.useState<string | null>(null);

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, patientId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuPatientId(patientId);
  };

  const closeRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuPatientId(null);
  };

  const viewPatientFromMenu = () => {
    if (menuPatientId) {
      navigate(`${ROUTES.PATIENT_BY_ID}/${menuPatientId}`);
    }
    closeRowMenu();
  };

  // Check for error query param (e.g., redirected from admin-frontend for non-superuser)
  React.useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'superuser_required') {
      setErrorSnackbar(t('dashboard.adminAccessDenied'));
      // Clear the error param from URL
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
      // Clear redirect flags to prevent loops
      sessionStorage.removeItem('admin_redirect_in_progress');
      sessionStorage.removeItem('demo_redirect_in_progress');
    }
  }, [searchParams, setSearchParams, t]);

  React.useEffect(() => {
    if (!user) return;
    setLoading(true);
    getUserPatients(String(user.user_id))
      .then((res) => setData(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    getSharedWithMe()
      .then((res) => setSharedWithMe(res.data || []))
      .catch((err) => console.error(err));
  }, [user]);

  const alertRows: AlertRow[] = React.useMemo(() => {
    return data.map((patient) => {
      const subjectId = String(patient.subject_id);
      const patientAny: any = patient as any;
      const derivedName = patientAny.fname || patientAny.lname ? `${(patientAny.fname||"")} ${(patientAny.lname||"")}`.trim() : undefined;
      const defaultName = derivedName || `Patient ${subjectId}`;
      const initials = defaultName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
      const profile = {
        name: defaultName,
        address: patientAny.address || "",
        email: patientAny.email || "",
        phone: patientAny.phone || "",
        color: ColorsEnum.warmgray4,
        image: "",
      };
      const profileFromPatient = derivedName
        ? {
            name: derivedName,
            address: patientAny.address || "",
            email: patientAny.email || "",
            phone: patientAny.phone || "",
            color: profile.color || ColorsEnum.warmgray4,
            image: profile.image,
          }
        : undefined;

      // use uuid in URL when available
      const urlId = (patientAny.uuid && String(patientAny.uuid)) || String(patient.subject_id);

      return {
        id: `AL-${patient.subject_id}`,
        patient_id: urlId,
        patient: {
          name: profileFromPatient?.name || profile.name,
          metadata: `Age ${patient.age || "-"} • ${patient.gender || "-"} • ${patientAny.city || ""}${patientAny.state ? ', ' + patientAny.state : ''}`,
          initials: profileFromPatient
            ? (profileFromPatient.name || "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
            : initials,
          avatar: profileFromPatient ? profileFromPatient.image : profile.image,
          profile: profileFromPatient || profile,
          age: patient.age ?? undefined,
          gender: patient.gender ?? undefined,
          location: (patientAny.city ? `${patientAny.city}${patientAny.state ? ', ' + patientAny.state : ''}` : undefined) || undefined,
        },
      };
    });
  }, [data]);

  const filteredAlertRows: AlertRow[] = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return alertRows;
    return alertRows.filter((row) => row.patient.name.toLowerCase().includes(query));
  }, [alertRows, searchQuery]);

  return (
    <>
      <TopToolbar searchValue={searchQuery} onSearchChange={setSearchQuery} />
      <Box p={3} pt={2} pb={1}>
        <Typography variant="h5" fontWeight={700} mb={1}>
          {t('dashboard.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('dashboard.subtitle')}
        </Typography>
      </Box>
      {sharedWithMe.length > 0 && (
        <Box px={3} pb={1}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            {t('share.sharedWithMe')}
          </Typography>
          <Paper sx={{ borderRadius: 2, boxShadow: "0 12px 30px rgba(0,0,0,0.08)" }}>
            <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
              {sharedWithMe.map((c) => (
                <Box
                  key={c.conversation_id}
                  onClick={() => navigate(`${ROUTES.CONVERSATION}/${c.short_code}`)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ mr: 2 }}>
                    {c.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatDate(c.updated_at, language)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}
      <Box p={3} pt={1}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, px: 1 }}>{t('dashboard.patient')}</TableCell>
                <TableCell sx={{ py: 0.25, px: 1 }}>{t('dashboard.age')}</TableCell>
                <TableCell sx={{ py: 0.25, px: 1 }}>{t('dashboard.gender')}</TableCell>
                <TableCell sx={{ py: 0.25, px: 1 }}>{t('dashboard.location')}</TableCell>
                <TableCell align="right" sx={{ py: 0.25, px: 1 }}>{t('dashboard.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <TableRow key={`skeleton-${idx}`}>
                      <TableCell sx={{ py: 0.25, px: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Skeleton variant="circular" width={24} height={24} />
                          <Box>
                            <Skeleton variant="text" width={120} />
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 1 }}>
                        <Skeleton variant="rounded" width={40} height={20} />
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 1 }}>
                        <Skeleton variant="rounded" width={40} height={20} />
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 1 }}>
                        <Skeleton variant="rounded" width={80} height={20} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25, px: 1 }}>
                        <Skeleton variant="circular" width={28} height={28} />
                      </TableCell>
                    </TableRow>
                  ))
                : filteredAlertRows.map((row) => {
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`${ROUTES.PATIENT_BY_ID}/${row.patient_id}`)}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: alpha(ColorsEnum.primary, 0.04),
                          },
                        }}
                      >
                        <TableCell sx={{ py: 0.25, px: 1 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {row.patient.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.25, px: 1 }}>
                          <Typography variant="body2">{(row.patient as any).age ?? "-"}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.25, px: 1 }}>
                          <Typography variant="body2">{(row.patient as any).gender ?? "-"}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.25, px: 1 }}>
                          <Typography variant="body2">{(row.patient as any).location || "-"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.25, px: 1 }}>
                          <IconButton onClick={(e) => openRowMenu(e, row.patient_id)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeRowMenu}>
        <MenuItem onClick={viewPatientFromMenu}>{t('dashboard.viewPatient')}</MenuItem>
      </Menu>

      {/* Error snackbar for admin access denial */}
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorSnackbar(null)} severity="warning" sx={{ width: '100%' }}>
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </>
  );
}
